// @flow

import React, { type Node, useMemo, useCallback, useState } from 'react';
import styled from 'styled-components';

import { InputMask, formatValueWithMask, type Mask } from '@tectonics/components/input-mask';
import { useUniqueId } from '@tectonics/hooks';
import { extractCardBin } from '@tectonics/commons/card';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { type CardBrand } from '@tectonics/prs/card-brand';
import { hasFeature } from '@tectonics/prs/feature';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSelectedCardBrand } from '@tectonics/look-and-feel/commons/selected-card-brand';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useRequestFetchCustomReceipts } from '@tectonics/prs/custom-receipts';
import { TextField } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { CardIcon } from './card-icon';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { getReceipts, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { getSafeInstalmentsStrategy } from '@tectonics/prs/receipts';
import { translate } from '@tectonics/translate';

export function FieldCardNumber(): Node {
  const inputId = useUniqueId();
  const cardBrand = useSelectedCardBrand();
  const cardMask = useMemo(() => cardBrandMask(cardBrand), [cardBrand]);
  const formatRaw = useCallback((rawValue: string) => formatValueWithMask(rawValue, cardMask), [cardMask]);
  const [value, handleChange] = useUserInputAsTextFieldWithFormatting('cardNumber', formatRaw, parseFormatted);
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardNumber');
  const [isFocused, setIsFocused] = useState <boolean> (false);
  const shouldHideCardNumber = useShouldHideCardNumber(cardBrand, isFocused);
  const requestFetchCustomReceiptsOnCardBinChange = useRequestFetchCustomReceiptsOnCardBinChange(value, validationError);
  const submitting = useSubmitting();

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    requestFetchCustomReceiptsOnCardBinChange();
  }, [setIsFocused, requestFetchCustomReceiptsOnCardBinChange]);

  return (
    <Root data-test-name="card-number">
      <label htmlFor={inputId}>
        <LabelMessage />
      </label>

      <InputHolder>
        <TextField
          as={InputMask}
          id={inputId}
          type={shouldHideCardNumber ? 'password' : 'tel'}
          mask={cardMask}
          value={value}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          fashion={formHasBeenSent && validationError ? 'error' : null}
          readOnly={submitting}
          data-error={formHasBeenSent && validationError ? 'error' : null}
          data-secret
          name="card-number"
        />
        <IconWrapper>
          <CardIcon brand={cardBrand} />
        </IconWrapper>
      </InputHolder>

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </Root>
  );
}

function useShouldHideCardNumber(cardBrand: ?CardBrand, isFocused: boolean): boolean {
  const allFeatures = useAllFeatures();

  return useMemo(() => {
    const hasHideAmexCardNumberFeature = hasFeature(allFeatures, 'hide_amex_card_number');
    return cardBrand === 'amex' && hasHideAmexCardNumberFeature && !isFocused;
  }, [cardBrand, isFocused, allFeatures]);
}

function useRequestFetchCustomReceiptsOnCardBinChange(
  cardNumber: string,
  validationError: ?UserInputValidationError,
): () => void {
  const incomingPayment = useIncomingPayment();
  const instalmentsStrategy = getSafeInstalmentsStrategy(getReceipts(incomingPayment));
  const [lastCardBinFetched, setLastCardBinFetched] = useState <string> ('');
  const requestFetchCustomReceipts = useRequestFetchCustomReceipts();

  return useCallback(() => {
    let cardBin = '';
    try {
      cardBin = extractCardBin(cardNumber);
    } catch (error) {
      return;
    }

    if (validationError || lastCardBinFetched === cardBin)
      return;

    if (instalmentsStrategy === 'dynamic-card-bin') {
      requestFetchCustomReceipts();
      setLastCardBinFetched(cardBin);
    }
  }, [cardNumber, validationError, lastCardBinFetched, setLastCardBinFetched]);
}

function LabelMessage(): string {
  return translate('volcanes.payment-methods.card.cardNumberLabel');
}

const Root = styled.div``;

const InputHolder = styled.div`
  position: relative;
`;

const IconWrapper = styled.div`
  position: absolute;
  bottom: 10px;
  pointer-events: none;

  [dir=ltr] & {
    right: 16px;
  }

  [dir=rtl] & {
    left: 16px;
  }
`;

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch (validationError.id) {
    case 'empty':
      return translate('volcanes.validation-error.fieldCardNumberMessageErrors.emptyMessage');

    default:
      return translate('volcanes.validation-error.fieldCardNumberMessageErrors.defaultMessage');
  }
};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError) || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}

function parseFormatted(formattedValue: string): string {
  return formattedValue.replace(/[^0-9]+/ug, '');
}

function cardBrandMask(cardBrand: ?CardBrand): Mask {
  switch (cardBrand) {
    case 'amex':
      return AMEX_MASK;

    case 'diners':
      return DINERS_MASK;

    default:
      return DEFAULT_MASK;
  }
}

const AMEX_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const DINERS_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const SIXTEEN_DIGITS_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const NINETEEN_DIGITS_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
  ' ',
  /[0-9]/, /[0-9]/, /[0-9]/,
];

const DEFAULT_MASK = (value) => {
  const onlyNumbers = value.replace(/\D/g, '');
  return onlyNumbers.length <= 16 ? SIXTEEN_DIGITS_MASK : NINETEEN_DIGITS_MASK;
};
